'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
  phone: string;
  serviceType: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  serviceType?: string;
  message?: string;
}

const serviceTypes = [
  'Patio Cleaning',
  'Pool Deck Cleaning',
  'Driveway Cleaning',
  'Paver Restoration',
  'Furniture Cleaning',
  'Commercial Services',
  'Other'
];

export default function ContactForm() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.serviceType) {
      newErrors.serviceType = 'Please select a service type';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        serviceType: '',
        message: ''
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  if (!isHydrated) {
    return (
      <section className="py-16 md:py-20 bg-muted">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-card border border-border rounded-lg p-8 md:p-12">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-full"></div>
              <div className="space-y-4">
                <div className="h-12 bg-muted rounded"></div>
                <div className="h-12 bg-muted rounded"></div>
                <div className="h-12 bg-muted rounded"></div>
                <div className="h-32 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-20 bg-muted">
      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="bg-card border border-border rounded-lg p-8 md:p-12 shadow-brand">
          <div className="text-center mb-8">
            <h2 className="font-headline font-bold text-3xl md:text-4xl text-foreground mb-4">
              Send Us a Message
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Fill out the form below and we'll get back to you within 2 hours during business hours.
            </p>
          </div>

          {submitSuccess && (
            <div className="mb-6 p-4 bg-success/10 border border-success rounded-md flex items-start space-x-3">
              <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-body font-semibold text-success mb-1">Message Sent Successfully!</h3>
                <p className="font-body text-sm text-success/80">
                  Thank you for contacting us. We'll respond to your inquiry within 2 hours.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block font-body font-medium text-foreground mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-md font-body focus:outline-none focus:ring-2 focus:ring-primary transition-brand ${
                    errors.name ? 'border-error' : 'border-input'
                  }`}
                  placeholder="John Doe"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-error font-body">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block font-body font-medium text-foreground mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-md font-body focus:outline-none focus:ring-2 focus:ring-primary transition-brand ${
                    errors.email ? 'border-error' : 'border-input'
                  }`}
                  placeholder="john@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-error font-body">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block font-body font-medium text-foreground mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-md font-body focus:outline-none focus:ring-2 focus:ring-primary transition-brand ${
                    errors.phone ? 'border-error' : 'border-input'
                  }`}
                  placeholder="(602) 555-1234"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-error font-body">{errors.phone}</p>
                )}
              </div>

              <div>
                <label htmlFor="serviceType" className="block font-body font-medium text-foreground mb-2">
                  Service Type *
                </label>
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-md font-body focus:outline-none focus:ring-2 focus:ring-primary transition-brand ${
                    errors.serviceType ? 'border-error' : 'border-input'
                  }`}
                >
                  <option value="">Select a service</option>
                  {serviceTypes.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
                {errors.serviceType && (
                  <p className="mt-1 text-sm text-error font-body">{errors.serviceType}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block font-body font-medium text-foreground mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={6}
                className={`w-full px-4 py-3 border rounded-md font-body focus:outline-none focus:ring-2 focus:ring-primary transition-brand resize-none ${
                  errors.message ? 'border-error' : 'border-input'
                }`}
                placeholder="Tell us about your patio cleaning needs..."
              />
              {errors.message && (
                <p className="mt-1 text-sm text-error font-body">{errors.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-accent text-accent-foreground px-8 py-4 rounded-md font-body font-semibold hover:bg-accent/90 transition-brand shadow-cta-glow hover:shadow-cta-glow-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Icon name="PaperAirplaneIcon" size={20} />
                  <span>Send Message</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}